import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    exchangeConsentLinkToken,
    getScopedAuthorizeNetKeys,
    getScopedConsent,
    getScopedPaymentCardStatus,
} from "../services/consent-link.services";
import {
    ConsentLinkAuthorizeNetKeys,
    ConsentLinkPaymentCardStatus,
    IConsentFormRecord,
} from "../types/consent-link.types";

export const CONSENT_LINK_QUERY_KEY = "consent-link";

export const consentLinkQueryKeys = {
    exchange: [CONSENT_LINK_QUERY_KEY, "exchange"],
    consent: (consentId: string) => [
        CONSENT_LINK_QUERY_KEY,
        consentId,
        "consent",
    ],
    paymentCardStatus: (consentId: string) => [
        CONSENT_LINK_QUERY_KEY,
        consentId,
        "payment-card-status",
    ],
    authorizeNetKeys: (consentId: string) => [
        CONSENT_LINK_QUERY_KEY,
        consentId,
        "authorize-net-keys",
    ],
};

export function useConsentLinkExchange(token: string | null) {
    return useQuery({
        queryKey: consentLinkQueryKeys.exchange,
        queryFn: () => {
            if (!token) {
                throw new Error("Consent token is required");
            }

            return exchangeConsentLinkToken(token);
        },
        enabled: Boolean(token),
        staleTime: Infinity,
        retry: false,
        onSuccess: () => {
            window.history.replaceState(
                window.history.state,
                "",
                `${window.location.pathname}${window.location.search}`
            );
        },
    });
}

export function useScopedConsent(consentId: string, enabled = true) {
    return useQuery<IApiResponse<IConsentFormRecord>, AxiosError>({
        queryKey: consentLinkQueryKeys.consent(consentId),
        queryFn: getScopedConsent,
        enabled: Boolean(consentId) && enabled,
    });
}

export function useScopedPaymentCardStatus(consentId: string, enabled = true) {
    return useQuery<ConsentLinkPaymentCardStatus, AxiosError>({
        queryKey: consentLinkQueryKeys.paymentCardStatus(consentId),
        queryFn: getScopedPaymentCardStatus,
        enabled: Boolean(consentId) && enabled,
    });
}

export function useScopedAuthorizeNetKeys(consentId: string, enabled = true) {
    return useQuery<ConsentLinkAuthorizeNetKeys, AxiosError>({
        queryKey: consentLinkQueryKeys.authorizeNetKeys(consentId),
        queryFn: getScopedAuthorizeNetKeys,
        enabled: Boolean(consentId) && enabled,
    });
}
